if (!customElements.get('product-reviews')) {
  customElements.define('product-reviews', class ProductReviews extends HTMLElement {
    async connectedCallback() {
      this.csvUrl = this.dataset.csv;
      this.jsonUrl = this.dataset.json;
      this.handle = this.dataset.productHandle || '';
      this.cols = parseInt(this.dataset.colsDesktop, 10) || 3;
      this.pageRows = parseInt(this.dataset.pageRows, 10) || 3;
      this.pageSize = this.cols * this.pageRows;
      this.shown = 0;
      this.reviews = [];

      this.grid = this.querySelector('[data-reviews-grid]');
      this.loading = this.querySelector('[data-reviews-loading]');
      this.empty = this.querySelector('[data-reviews-empty]');
      this.moreWrap = this.querySelector('[data-reviews-more-wrap]');
      this.moreBtn = this.querySelector('[data-reviews-more]');
      this.summary = this.querySelector('[data-reviews-summary]');
      this.summaryAvg = this.querySelector('[data-reviews-avg]');
      this.summaryStarsFg = this.querySelector('[data-reviews-stars-fg]');
      this.summaryCount = this.querySelector('[data-reviews-count]');

      this.style.setProperty('--reviews-cols-desktop', this.cols);

      this.reviews = await this.loadReviews();

      if (!this.reviews.length) {
        this.finishLoading(true);
        return;
      }

      this.finishLoading(false);
      this.renderSummary();
      this.renderMore();
      this.moreBtn.addEventListener('click', () => this.renderMore());
    }

    renderSummary() {
      if (!this.summary || !this.reviews.length) return;
      const total = this.reviews.length;
      const sum = this.reviews.reduce((acc, r) => acc + (Number(r.yildiz) || 0), 0);
      const avg = sum / total;
      const avgStr = (Math.round(avg * 10) / 10).toFixed(1).replace('.', ',');
      const fillPct = Math.max(0, Math.min(100, (avg / 5) * 100));

      if (this.summaryAvg) this.summaryAvg.textContent = avgStr;
      if (this.summaryStarsFg) this.summaryStarsFg.style.width = fillPct + '%';
      if (this.summaryCount) this.summaryCount.textContent = '(' + total + ' Yorum)';
      this.summary.hidden = false;
    }

    async loadReviews() {
      if (this.jsonUrl) {
        try {
          const res = await fetch(this.jsonUrl, { cache: 'no-store' });
          if (res.ok) {
            const data = await res.json();
            if (Array.isArray(data) && data.length) {
              console.info('[product-reviews] Loaded JSON for', this.handle);
              return data.map((r) => ({
                yorum: (r.yorum || r.comment || r.review || r.text || '').toString().trim(),
                yildiz: this.toRating(r.yildiz != null ? r.yildiz : (r.star != null ? r.star : r.rating)),
                kullanici: (r.kullanici || r.user || r.name || r.isim || '').toString().trim(),
              })).filter((r) => r.yorum || r.kullanici);
            }
          }
        } catch (e) {
          console.warn('[product-reviews] JSON load error', e);
        }
      }

      if (this.csvUrl) {
        try {
          const res = await fetch(this.csvUrl, { cache: 'no-store' });
          if (res.ok) {
            const text = await res.text();
            const parsed = this.parseCSV(text);
            if (parsed.length) {
              console.info('[product-reviews] Loaded CSV for', this.handle);
              return parsed;
            }
          } else {
            console.warn('[product-reviews] CSV not found at', this.csvUrl, '(' + res.status + ')');
          }
        } catch (e) {
          console.warn('[product-reviews] CSV load error', e);
        }
      }

      return [];
    }

    finishLoading(showEmpty) {
      if (this.loading) this.loading.hidden = true;
      if (showEmpty) {
        this.empty.hidden = false;
      } else {
        this.grid.hidden = false;
      }
    }

    parseCSV(text) {
      const lines = this.splitLines(text);
      if (lines.length < 2) return [];

      const headers = this.splitRow(lines[0]).map((h) => h.trim().toLowerCase());
      const findIdx = (patterns) => headers.findIndex((h) => patterns.some((p) => h === p || h.includes(p)));

      const yorumIdx = findIdx(['yorum', 'comment', 'review', 'message', 'metin']);
      const yildizIdx = findIdx(['yildiz', 'yıldız', 'star', 'rating', 'puan']);
      const kullaniciIdx = findIdx(['kullanici', 'kullanıcı', 'user', 'name', 'isim', 'ad']);

      return lines
        .slice(1)
        .map((line) => {
          const cols = this.splitRow(line);
          return {
            yorum: yorumIdx >= 0 ? (cols[yorumIdx] || '').trim() : '',
            yildiz: yildizIdx >= 0 ? this.toRating(cols[yildizIdx]) : 5,
            kullanici: kullaniciIdx >= 0 ? (cols[kullaniciIdx] || '').trim() : '',
          };
        })
        .filter((r) => r.yorum || r.kullanici);
    }

    toRating(val) {
      const n = parseFloat((val || '').replace(',', '.'));
      if (isNaN(n)) return 5;
      return Math.max(0, Math.min(5, Math.round(n)));
    }

    splitLines(text) {
      return text
        .replace(/^﻿/, '')
        .split(/\r?\n/)
        .filter((l) => l.trim().length > 0);
    }

    splitRow(row) {
      const result = [];
      let cur = '';
      let inQuote = false;
      for (let i = 0; i < row.length; i++) {
        const ch = row[i];
        if (ch === '"') {
          if (inQuote && row[i + 1] === '"') {
            cur += '"';
            i++;
          } else {
            inQuote = !inQuote;
          }
        } else if (ch === ',' && !inQuote) {
          result.push(cur);
          cur = '';
        } else {
          cur += ch;
        }
      }
      result.push(cur);
      return result;
    }

    renderMore() {
      const next = this.reviews.slice(this.shown, this.shown + this.pageSize);
      const frag = document.createDocumentFragment();
      next.forEach((r) => frag.appendChild(this.renderCard(r)));
      this.grid.appendChild(frag);
      this.shown += next.length;

      this.moreWrap.hidden = this.shown >= this.reviews.length;
    }

    renderCard(r) {
      const card = document.createElement('article');
      card.className = 'product-reviews__card';

      const stars = document.createElement('div');
      stars.className = 'product-reviews__stars';
      stars.setAttribute('aria-label', r.yildiz + '/5 yıldız');
      for (let i = 1; i <= 5; i++) {
        const star = document.createElement('span');
        star.className = 'product-reviews__star' + (i <= r.yildiz ? ' is-filled' : '');
        star.textContent = '★';
        star.setAttribute('aria-hidden', 'true');
        stars.appendChild(star);
      }
      card.appendChild(stars);

      if (r.yorum) {
        const text = document.createElement('p');
        text.className = 'product-reviews__text';
        text.textContent = r.yorum;
        card.appendChild(text);
      }

      if (r.kullanici) {
        const name = document.createElement('div');
        name.className = 'product-reviews__name';
        name.textContent = r.kullanici;
        card.appendChild(name);
      }

      return card;
    }
  });
}

if (!customElements.get('product-rating-summary')) {
  customElements.define('product-rating-summary', class ProductRatingSummary extends HTMLElement {
    async connectedCallback() {
      this.csvUrl = this.dataset.csv;
      this.jsonUrl = this.dataset.json;
      this.linkToReviews = this.dataset.linkToReviews === '1';

      this.avgEl = this.querySelector('[data-rating-avg]');
      this.fgEl = this.querySelector('[data-rating-fg]');
      this.countEl = this.querySelector('[data-rating-count]');

      const reviews = await this.loadReviews();
      if (!reviews.length) {
        this.hidden = true;
        return;
      }

      const total = reviews.length;
      const sum = reviews.reduce((acc, r) => acc + (Number(r.yildiz) || 0), 0);
      const avg = sum / total;
      const avgStr = (Math.round(avg * 10) / 10).toFixed(1).replace('.', ',');
      const fillPct = Math.max(0, Math.min(100, (avg / 5) * 100));

      if (this.avgEl) this.avgEl.textContent = avgStr;
      if (this.fgEl) this.fgEl.style.width = fillPct + '%';
      if (this.countEl) this.countEl.textContent = '(' + total + ' Yorum)';
      this.hidden = false;

      if (this.linkToReviews) {
        this.style.cursor = 'pointer';
        this.setAttribute('role', 'button');
        this.setAttribute('tabindex', '0');
        const scroll = () => {
          const target = document.querySelector('product-reviews');
          if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        };
        this.addEventListener('click', scroll);
        this.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            scroll();
          }
        });
      }
    }

    async loadReviews() {
      if (this.jsonUrl) {
        try {
          const res = await fetch(this.jsonUrl, { cache: 'no-store' });
          if (res.ok) {
            const data = await res.json();
            if (Array.isArray(data) && data.length) {
              return data.map((r) => ({
                yildiz: this.toRating(r.yildiz != null ? r.yildiz : (r.star != null ? r.star : r.rating)),
              }));
            }
          }
        } catch (e) { /* fall through */ }
      }

      if (this.csvUrl) {
        try {
          const res = await fetch(this.csvUrl, { cache: 'no-store' });
          if (res.ok) {
            const text = await res.text();
            const lines = text.replace(/^﻿/, '').split(/\r?\n/).filter((l) => l.trim().length > 0);
            if (lines.length >= 2) {
              const headers = this.splitRow(lines[0]).map((h) => h.trim().toLowerCase());
              const idx = headers.findIndex((h) => /yildiz|yıldız|star|rating|puan/.test(h));
              if (idx >= 0) {
                return lines.slice(1).map((line) => ({
                  yildiz: this.toRating(this.splitRow(line)[idx]),
                }));
              }
            }
          }
        } catch (e) { /* fall through */ }
      }

      return [];
    }

    toRating(val) {
      const n = parseFloat((val || '').toString().replace(',', '.'));
      if (isNaN(n)) return 5;
      return Math.max(0, Math.min(5, Math.round(n)));
    }

    splitRow(row) {
      const result = [];
      let cur = '';
      let inQuote = false;
      for (let i = 0; i < row.length; i++) {
        const ch = row[i];
        if (ch === '"') {
          if (inQuote && row[i + 1] === '"') { cur += '"'; i++; }
          else inQuote = !inQuote;
        } else if (ch === ',' && !inQuote) {
          result.push(cur);
          cur = '';
        } else {
          cur += ch;
        }
      }
      result.push(cur);
      return result;
    }
  });
}
